import empty from "is-empty";
import Humanize from "humanize-plus";

import { FETCH_STATES } from "src/redux/Tx/constants";
import * as Validators from "src/utils/validators";
import * as Formatters from "src/components/common/AgGrid/formatters";
import { setDateFormat } from "src/utils";
import { intOrDash, numberOrDash } from "src/utils/number";
import getHeaderValue from "src/components/common/AgGrid/headerGetter";
import { TxSelectors } from "src/redux/Tx/selectors";

import styles from "src/styles/agGridCustom.module.scss";
import { ROLE } from "src/redux/TopBar/constants";
import {
    isQuantityValid,
    isListNameValid,
    TX_PURPOSE_SELECT_OPTIONS,
    TX_PURPOSE_SELECT_LABELS,
} from "src/utils/validators";
import { formatQuantity } from "src/components/common/AgGrid/formatters";
import { store } from "src/index";
import {
    DEFAULT_CCY_OPTIONS,
    HUMANIZED_INTERMEDIARY,
    HUMANIZED_TX_TYPES,
} from "src/redux/Confirmations/constants";
import DropdownItemRenderer from "src/components/common/AgGrid/DropdownItemRenderer";
import { isEmpty } from "lodash";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";

export const RowClassRules = {
    [styles.agRowError]: (params) =>
        !Validators.isTxInitRowValid(params.data, params.context.role) ||
        params.data.isRemoved,
};

export const CommonColumnDefs = {
    resizable: true,
    suppressMenu: true,
    sortable: true,
    tooltipComponent: "customTooltip",
};

const isPortfolioConstituent = (params) =>
    params.context?.isPortfolioTx && params.data?.parentId !== null;

export const getTXPreviewColumnDefs = (isLender, isPortfolioTx) => {
    const {
        "enable-purpose": enabledPurpose,
        "enable-90-day-max-maturity-date": enable90DayMaxMaturity,
    } = SettingsSelectors.FeatureFlags(store.getState());

    let columns = [
        {
            headerName: "Num",
            colId: "Create Num",
            valueGetter: (params) => params.node.rowIndex + 1,
            width: 60,
            minWidth: 60,
            flex: 6,
            sortable: false,
            cellClass: [styles.agCenterAlign],
            cellRendererSelector: (params) => {
                if (params.context && params.context.isPortfolioTx) {
                    return { component: "txGroupExpand" };
                }
                // Default: return undefined to use default rendering
                return undefined;
            },
        },
        {
            headerName: "Security ID",
            field: "SecurityId",
            colId: "Create Ticker",
            minWidth: 140,
            flex: 14,
            cellClassRules: {
                [styles.agCellError]: (params) =>
                    !Validators.isMarketDataValid(params.data.marketData),
                [styles.agCellWarning]: (params) =>
                    !Validators.isAllowedLenderSecurity(
                        isLender,
                        params.data.marketData
                    ),
            },
            cellClass: ["data-type-string"],
            valueParser: (params) => params.newValue?.trim(),
            tooltipField: "SecurityId",
            tooltipComponentParams: { type: "MD", skipShowingValueDate: true },
        },
        // A hidden column: Sort by removed first
        {
            headerName: "Removed",
            field: "isRemoved",
            colId: "SortRemoved",
            sortable: true,
            sort: "desc",
            sortIndex: 0,
            hide: true,
        },
        // Followed by securites not found
        {
            headerName: "NotFound",
            field: "isNotFound",
            colId: "SortNotFound",
            sortable: true,
            sort: "desc",
            sortIndex: 1,
            hide: true,
            valueGetter: (params) => {
                return params.data?.marketData?.status === FETCH_STATES.ERROR;
            },
        },
        {
            headerName: "Security Name",
            field: "Name",
            colId: "Create Sec Name",
            minWidth: 200,
            flex: 20,
            sort: isPortfolioTx ? "asc" : undefined,
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params.data.marketData.status === FETCH_STATES.FETCHING,
                [styles.agCellWarning]: (params) =>
                    !Validators.isAllowedLenderSecurity(
                        isLender,
                        params.data.marketData
                    ),
            },
            tooltipField: "Name",
            tooltipComponentParams: { type: "MD" },
            sortIndex: 2,
        },
        {
            headerName: "Security Name (Local)",
            field: "LocalName",
            colId: "Create Local Name",
            minWidth: 200,
            flex: 20,
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params.data.marketData.status === FETCH_STATES.FETCHING,
                [styles.agCellWarning]: (params) =>
                    !Validators.isAllowedLenderSecurity(
                        isLender,
                        params.data.marketData
                    ),
            },
            tooltipField: "LocalName",
            tooltipComponentParams: { type: "MD" },
        },
        isPortfolioTx
            ? {
                  headerName: "Constituents",
                  field: "isRemoved",
                  colId: "Create Constituents",
                  minWidth: 100,
                  flex: 10,
                  sortable: false,
                  cellClass: [styles.agCenterAlign],
                  cellRenderer: "constituentsRenderer",
              }
            : {},
        {
            headerName: "Benchmark Fee (bps)",
            field: "BenchmarkFee",
            colId: "Create Benchmark Fee",
            minWidth: 100,
            flex: 10,
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params.data.marketData.status === FETCH_STATES.FETCHING,
            },
            valueFormatter: (params) => {
                if (!params.value && params.value !== 0) return "-";
                if (isNaN(params.value)) return params.value;
                return Humanize.formatNumber(params.value, 2);
            },
        },
        isLender
            ? {
                  headerName: "List Name",
                  field: "ListName",
                  colId: "Create List Name",
                  width: 110,
                  minWidth: 110,
                  flex: 11,
                  editable: true,
                  cellClass: [styles.agEditable],
                  cellClassRules: {
                      [styles.agCellError]: (params) =>
                          !isListNameValid(params.value),
                  },
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: (params) => {
                      const usesClientPool = AuthSelectors.UsesClientPool(
                          store.getState()
                      );

                      const listNames = usesClientPool
                          ? SettingsSelectors.SelectableListNamesForBrokerCpty(
                                store.getState(),
                                params.data?.BbgCode
                            )
                          : params.context.listNames || [];

                      return {
                          values: listNames,
                          formatValue: (value) => (empty(value) ? "-" : value),
                          cellRenderer: DropdownItemRenderer,
                      };
                  },
                  tooltipValueGetter: (params) => ({ value: params.value }),
                  tooltipComponentParams: { type: "listName" },
              }
            : {},
        isLender
            ? {
                  headerName: "Net Qty",
                  field: "TruQuantity",
                  colId: "Create Tru Qty",
                  minWidth: 90,
                  flex: 9,
                  headerClass: [styles.agTruHeader],
                  cellClass: [styles.agRightAlign],
                  cellClassRules: {
                      [styles.agOverflow]: (params) =>
                          !isNaN(params.value) && params.value < 0,
                      [styles.agCellWarning]: (params) => {
                          // highlight cell for offers which exceed non-dynamic list inventory qty
                          if (
                              params.context.role === ROLE.LENDER &&
                              !empty(params.data?.ListName) &&
                              !Validators.isDynamicOrClientPoolList(
                                  params.data?.ListName
                              ) &&
                              !Validators.isCreateRowsTxQuantityBelowInventoryQty(
                                  store.getState(),
                                  params.data?.ListName,
                                  false,
                                  params.data?.security?.id,
                                  params.data?.security?.isin,
                                  params.context.role
                              )
                          )
                              return true;

                          return false;
                      },
                  },
                  valueFormatter: (params) => {
                      if (isNaN(params.value)) {
                          return "-";
                      }
                      return Humanize.intComma(params.value);
                  },
                  tooltipValueGetter: (params) => ({
                      value: params.data?.Quantity,
                      listName: params.data?.ListName,
                      // BUG: ISIN provided instead of SecurityId in the `SecurityId` column
                      ISIN: params.data?.SecurityId,
                      securityId: params.data?.security?.id,
                      role: params.context.role, // tootltipComponentParams cannot access context params
                  }),
                  tooltipComponentParams: (params) => ({
                      type: params.value.role === ROLE.LENDER ? "truQty" : "",
                  }),
              }
            : {},
        {
            headerValueGetter: (params) =>
                params.context.role === ROLE.LENDER
                    ? "Response Qty"
                    : "Request Qty",
            field: "Quantity",
            colId: "Create Qty",
            minWidth: 90,
            flex: 9,
            editable: (params) => !isPortfolioConstituent(params),
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellWarning]: (params) => {
                    // highlight cell for offers which exceed non-dynamic list inventory qty
                    if (
                        params.context.role === ROLE.LENDER &&
                        !empty(params.data?.ListName) &&
                        !Validators.isDynamicOrClientPoolList(
                            params.data?.ListName
                        ) &&
                        !Validators.isCreateRowsTxQuantityBelowInventoryQty(
                            store.getState(),
                            params.data?.ListName,
                            false,
                            params.data?.security?.id,
                            params.data?.security?.isin,
                            params.context.role
                        )
                    )
                        return true;

                    return false;
                },
                [styles.agCellError]: (params) => {
                    if (!isQuantityValid(params.value, params.data.Country))
                        return true;

                    let isDynamicOrClientPoolList =
                        Validators.isDynamicOrClientPoolList(
                            params.data?.ListName
                        );
                    if (
                        params.context.role === ROLE.LENDER &&
                        isDynamicOrClientPoolList
                    ) {
                        let totalOffered =
                            TxSelectors.CreateRowDataTxTotalOfferedQty(
                                store.getState(),
                                params.data?.ListName,
                                // Note: 'securityId' from the params is actually ISIN, BUG!
                                // but in TxSelectors.CreateRowDataTxTotalOfferedQty row.securityId is also isin
                                params.data?.SecurityId
                            );
                        if (
                            !Validators.isTxQuantityBelowInventoryQty(
                                params.data?.ListName,
                                isDynamicOrClientPoolList,
                                totalOffered,
                                // Note: 'securityId' from the params is actually ISIN, BUG!
                                params.data?.security?.id,
                                params.context.role
                            )
                        )
                            return true;
                    }

                    return false;
                },
            },
            valueParser: (params) =>
                Validators.parseIntToSizeLimit(params.newValue),
            valueGetter: (params) => {
                if (isPortfolioConstituent(params)) {
                    const parentQuantity =
                        params.node?.parent?.data?.Quantity ?? 1;
                    return parentQuantity * params.data.CuValue;
                }

                return params.data?.Quantity;
            },
            valueFormatter: (params) => {
                const formattedQty = formatQuantity(
                    params.value,
                    params.data.Country
                );

                // for parent rows of portfolio tx, append " CU"
                if (params.data && params.data.parentId === null) {
                    return `${formattedQty} CU`;
                }
                return formattedQty;
            },
            tooltipValueGetter: (params) => ({
                value: params.value,
                listName: params.data?.ListName,
                // BUG: ISIN provided instead of SecurityId in the `SecurityId` column
                ISIN: params.data?.SecurityId,
                securityId: params.data?.security?.id,
                role: params.context.role,
            }),
            tooltipComponentParams: (params) => ({
                type:
                    params.value.role === ROLE.LENDER ? "offerQty" : "quantity",
            }),
        },
        {
            headerValueGetter: (params) =>
                params.context.role === ROLE.LENDER
                    ? "Response Rate"
                    : "Request Rate (%)",
            field: "CalculatedRate",
            colId: "Create Rate",
            minWidth: 90,
            flex: 9,
            editable: (params) => !isPortfolioConstituent(params),
            cellClass: [styles.agRightAlign],
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: { type: "txCreateRate" },
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                // defeat the null check in the validator
                [styles.agCellError]: (params) =>
                    params.value && !Validators.isRateValid(params.value),
            },
            valueParser: (params) =>
                Validators.isRateValid(params.newValue)
                    ? parseFloat(params.newValue).toFixed(2)
                    : params.newValue,
            valueGetter: (params) => {
                if (isPortfolioConstituent(params)) {
                    return params.node?.parent?.data?.CalculatedRate;
                }

                return params.data?.CalculatedRate;
            },
            valueFormatter: (params) =>
                Formatters.formatRate(params.value, true),
        },
        {
            headerName: "V/D",
            field: "ValueDate",
            colId: "Create ValueDate",
            minWidth: 100,
            flex: 10,
            editable: (params) => !isPortfolioConstituent(params),
            cellEditor: "datePicker",
            cellEditorPopup: true,
            cellClass: [styles.agCenterAlign],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                // defeat the null check in the validator
                [styles.agCellError]: (params) =>
                    params.value &&
                    !Validators.isDateValid(
                        params.value,
                        params.data.TradeableDates
                    ),
            },
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: { type: "txCreateVD" },
            valueGetter: (params) => {
                if (isPortfolioConstituent(params)) {
                    return params.node?.parent?.data?.ValueDate;
                }

                return params.data?.ValueDate;
            },
            valueFormatter: (params) => setDateFormat(params.value, "/") || "-",
        },
        {
            headerName: "Utilization",
            field: "Utilization",
            colId: "Create Utilization",
            minWidth: 100,
            flex: 10,
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params.data.marketData.status === FETCH_STATES.FETCHING,
            },
            valueParser: (params) =>
                Validators.isRateValid(params.newValue)
                    ? parseFloat(params.newValue).toFixed(2)
                    : params.newValue,
            valueFormatter: (params) => {
                if (!params.value && params.value !== 0) return "-";
                if (isNaN(params.value)) return params.value;
                return Humanize.formatNumber(params.value, 2);
            },
        },
        isLender
            ? {
                  headerName: "Callable",
                  field: "Callable",
                  colId: "Create Callable",
                  minWidth: 70,
                  flex: 7,
                  editable: true,
                  cellClass: [styles.agEditable, styles.agCenterAlign],
                  valueFormatter: (params) => (params.value ? "Y" : "N"),
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: {
                      values: [true, false],
                      formatValue: (value) => (value ? "Y" : "N"),
                      cellRenderer: DropdownItemRenderer,
                  },
              }
            : {},
        isLender
            ? {
                  headerName: "Div (%)",
                  field: "Dividend",
                  colId: "Create Div",
                  minWidth: 70,
                  flex: 7,
                  editable: true,
                  cellClass: [styles.agEditable, styles.agRightAlign],
                  cellClassRules: {
                      [styles.agCellError]: (params) =>
                          !Validators.isDivLiabilityValid(params.value),
                  },
                  valueFormatter: (params) =>
                      numberOrDash({ value: params.value }),
              }
            : {},
        isLender
            ? {
                  headerName: "Coll Type",
                  field: "CollateralType",
                  colId: "Create Coll Type",
                  minWidth: 70,
                  flex: 7,
                  cellClass: [styles.agEditable, styles.agCenterAlign],
                  valueFormatter: (params) => params.value || "-",
                  editable: true,
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: {
                      values: ["CA", "NC", "CP"],
                      formatValue: (value) => (empty(value) ? "-" : value),
                      cellRenderer: DropdownItemRenderer,
                  },
              }
            : {},
        isLender
            ? {
                  headerName: "Coll CCY",
                  field: "CollateralCCY",
                  colId: "Create Coll Ccy",
                  minWidth: 70,
                  flex: 7,
                  cellClass: [styles.agEditable, styles.agCenterAlign],
                  valueFormatter: (params) => params.value || "-",
                  editable: true,
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: {
                      values: DEFAULT_CCY_OPTIONS,
                      formatValue: (value) => (empty(value) ? "-" : value),
                      cellRenderer: DropdownItemRenderer,
                  },
              }
            : {},
        isLender
            ? {
                  headerName: "F/R", // need to change as to the collateral type
                  colId: "Create FR",
                  field: "FR",
                  minWidth: 60,
                  flex: 6,
                  cellClass: [styles.agCenterAlign],
              }
            : {},
        isLender
            ? {
                  headerName: "Intermediary\n(중개기관)",
                  field: "Intermediary",
                  colId: "Create Intermediary",
                  minWidth: 90,
                  flex: 9,
                  editable: true,
                  headerClass: [styles.agCenterAlign],
                  cellClass: [styles.agEditable, styles.agCenterAlign],
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: (params) => ({
                      values: Validators.COMPATIBLE_INTERMEDIARY_VALUES(
                          params.data.TxType
                      ),
                      formatValue: (value) =>
                          HUMANIZED_INTERMEDIARY[value] ||
                          HUMANIZED_INTERMEDIARY.ANY,
                      cellRenderer: DropdownItemRenderer,
                  }),
                  valueFormatter: (params) =>
                      HUMANIZED_INTERMEDIARY[params.value] ||
                      HUMANIZED_INTERMEDIARY.ANY,
              }
            : {},
        isLender
            ? {
                  headerName: "Type",
                  field: "TxType",
                  colId: "Create TX Type",
                  width: 100,
                  minWidth: 100,
                  editable: true,
                  cellClass: [styles.agEditable, styles.agCenterAlign],
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: (params) => ({
                      values: Validators.COMPATIBLE_TX_TYPE_VALUES(
                          params.data.Intermediary
                      ),
                      formatValue: (value) =>
                          HUMANIZED_TX_TYPES[value] || HUMANIZED_TX_TYPES.ANY,
                      cellRenderer: DropdownItemRenderer,
                  }),
                  valueFormatter: (params) =>
                      HUMANIZED_TX_TYPES[params.value] ||
                      HUMANIZED_TX_TYPES.ANY,
              }
            : {},
        {
            headerName: "Maturity",
            field: "Maturity",
            colId: "Create Maturity",
            minWidth: 100,
            flex: 10,
            editable: (params) => !isPortfolioConstituent(params),
            cellEditor: "datePicker",
            cellEditorPopup: true,
            cellClass: [styles.agCenterAlign],
            cellClassRules: {
                [styles.agEditable]: (params) => params.colDef.editable(params),
                [styles.agCellError]: (params) =>
                    params.value &&
                    !Validators.isMaxMaturityValid(
                        params.data,
                        enable90DayMaxMaturity
                    ),
            },
            valueGetter: (params) => {
                if (isPortfolioConstituent(params)) {
                    return params.node?.parent?.data?.Maturity;
                }

                return params.data?.Maturity;
            },
            valueFormatter: (params) => setDateFormat(params.value, "/") || "-",
            tooltipField: "Maturity",
            tooltipComponentParams: { type: "Maturity" },
        },
        {
            headerName: "Days to Maturity",
            colId: "Create Days to Maturity",
            field: "Term",
            minWidth: 100,
            flex: 10,
            cellClass: [styles.agCenterAlign],
            valueGetter: (params) => {
                if (isPortfolioConstituent(params)) {
                    return params.node?.parent?.data?.Term;
                }

                return params.data?.Term;
            },
            valueFormatter: (params) =>
                params.value ? `${params.value} days` : "-",
        },
        !isLender && enabledPurpose
            ? {
                  headerName: "Purpose",
                  field: "Purpose",
                  colId: "Create Purpose",
                  editable: (params) => !isPortfolioConstituent(params),
                  cellClassRules: {
                      [styles.agEditable]: (params) =>
                          params.colDef.editable(params),
                      [styles.agCellError]: (params) =>
                          !Validators.isMarketDataValid(params.data.marketData),
                      [styles.agCellLoading]: (params) =>
                          params.data.marketData.status ===
                          FETCH_STATES.FETCHING,
                  },
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: () => ({
                      values: TX_PURPOSE_SELECT_OPTIONS(),
                      formatValue: TX_PURPOSE_SELECT_LABELS,
                      cellRenderer: DropdownItemRenderer,
                  }),
                  valueGetter: (params) => {
                      if (isPortfolioConstituent(params)) {
                          return params.node?.parent?.data?.Purpose;
                      }

                      return params.data?.Purpose;
                  },
                  valueFormatter: (params) =>
                      TX_PURPOSE_SELECT_LABELS(params.value),
              }
            : {},
        {
            headerValueGetter: (params) => getHeaderValue(params),
            headerName: "Value (<role>)",
            field: "Amount",
            colId: "Create Amount",
            minWidth: 140,
            flex: 14,
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params.data.marketData.status === FETCH_STATES.FETCHING,
            },
            valueGetter: (params) => {
                // for child rows of portfolio tx, display based on previous close * displayed qty
                if (
                    params.data &&
                    params.data.parentId !== null &&
                    params.context?.isPortfolioTx
                ) {
                    const previousClose =
                        params.data.marketData?.data?.closingPrice;
                    if (previousClose) {
                        const parentQuantity =
                            params.node?.parent?.data?.Quantity || 1;
                        const displayedQuantity =
                            parentQuantity * params.data.Quantity;
                        return previousClose * displayedQuantity;
                    }
                }

                return params.data?.Amount;
            },
            valueFormatter: (params) =>
                intOrDash({
                    value: params.value,
                    replacement: "ERROR",
                }),
        },
        {
            headerName: "Country",
            field: "Country",
            colId: "Create Country",
            width: 80,
            minWidth: 80,
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params.data.marketData.status === FETCH_STATES.FETCHING,
            },
        },
        {
            headerValueGetter: (params) => getHeaderValue(params),
            headerName: "Prev Close",
            field: "PreviousClose",
            colId: "Create Prev Close",
            minWidth: 100,
            flex: 10,
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params.data.marketData.status === FETCH_STATES.FETCHING,
            },
            valueFormatter: (params) => {
                return params.value || "ERROR";
            },
        },
        !isPortfolioTx
            ? {
                  headerName: "Delete",
                  field: "Delete",
                  colId: "Create Delete",
                  width: 60,
                  cellRenderer: "deleteButton",
              }
            : {},
    ];
    return columns.filter((col) => !isEmpty(col));
};
